import classnames from 'classnames'
import LoaderBalls from 'components/LoaderBalls'
import { pathOr } from 'ramda'
import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useOnClickOutside from 'use-onclickoutside'

import 'emoji-mart/css/emoji-mart.css'
import './style.css'

const createEmojiMartI18nProp = (t) => ({
  search: t('emoji.search'),
  clear: t('emoji.clear'), // Accessible label on "clear" button
  notfound: t('emoji.notfound'),
  skintext: t('emoji.skintext'),
  categories: {
    search: t('emoji.category.search'),
    recent: t('emoji.category.recent'),
    smileys: t('emoji.category.smileys'),
    people: t('emoji.category.smileys'),
    nature: t('emoji.category.nature'),
    foods: t('emoji.category.foods'),
    activity: t('emoji.category.activity'),
    places: t('emoji.category.places'),
    objects: t('emoji.category.objects'),
    symbols: t('emoji.category.symbols'),
    flags: t('emoji.category.flags'),
    custom: t('emoji.category.custom'),
  },
  categorieslabel: t('emoji.categorieslabel'), // Accessible title for the list of categories
  skintones: {
    '1': t('emoji.skintone1'),
    '2': t('emoji.skintone2'),
    '3': t('emoji.skintone3'),
    '4': t('emoji.skintone4'),
    '5': t('emoji.skintone5'),
    '6': t('emoji.skintone6'),
  }
})

const LazyLoadedPicker = lazy(() => import('./picker'))

type EmojiPickerProps = {
  open: boolean,
  onAddEmoji: Function,
  onClickOutside: Function
}

const EmojiPickerLoader = ({ loadingText }) => (
  <div className="drift-widget-emoji-picker--loader">
    <p>{loadingText}</p>
    <LoaderBalls/>
  </div>
)

const EmojiPicker = ({
  open,
  onAddEmoji,
  onClickOutside
}: EmojiPickerProps) => {
  const { t } = useTranslation()
  const { activeConversation: activeConversationId } = useSelector(state => state.conversations)
  const locale = useSelector(state => (
    pathOr('en', ['embed', 'configuration', 'locale'], state)
  ))
  const hideSearch = locale !== 'en'
  const emojiI18n = useMemo(() => createEmojiMartI18nProp(t), [t])
  const emojiRef = useRef(null)
  const [emojiData, setEmojiData] = useState(null)

  useEffect(() => {
    const fetchEmojiData = async () => {
      await import('emoji-mart/data/apple.json')
        .then(res => setEmojiData(res.default))
    }

    if(open && !emojiData) {
      fetchEmojiData()
    }
  }, [open, emojiData])

  useOnClickOutside(emojiRef, (event) => {
    onClickOutside(event)
  })

  const addEmojiToMessage = ({native: nativeEmoji }) => {
    onAddEmoji(message => `${message} ${nativeEmoji}`)
  }

  return (
    <div ref={emojiRef} className={classnames("drift-widget-composer-emoji-picker", {
      "drift-widget-composer-emoji-picker--open": open,
      "drift-widget-composer-emoji-picker--full": activeConversationId,
      "drift-widget-composer-emoji-picker--mini": !activeConversationId,
      "drift-widget-composer-emoji-picker--hide-search": hideSearch,
    })}>
      <Suspense fallback={<EmojiPickerLoader loadingText={t('status.loading')}/>}>
        { !!emojiData &&
          <LazyLoadedPicker
            darkMode={false}
            hideSearch={hideSearch}
            onSelect={addEmojiToMessage}
            sheetSize={64}
            set='apple'
            data={emojiData}
            exclude={['flags', 'symbols', 'objects']}
            perLine={6}
            emojiTooltip={!hideSearch}
            i18n={emojiI18n}
            showPreview={false}
            showSkinTones={false}
            onClickOutside={onClickOutside}
          />
        }
      </Suspense>
    </div>
  )
}

export default EmojiPicker
